import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { PublicLayout } from '@/components/public/PublicLayout';
import { ReviewLandingScreen } from '@/components/public/review/ReviewLandingScreen';
import { TokenExpiredState } from '@/components/public/confirm/TokenExpiredState';
import { useReviewLanding } from '@/hooks/public/useReviewLanding';

/**
 * Public landing page for review-request short links.
 *
 * Backend mounts this under `/v2/public/reviews` (see
 * `apps/operations/src/modules/reviews/public-reviews.controller.ts`).
 *
 * This route is a shell and nothing more: resolve the token, then hand the
 * payload to `ReviewLandingScreen`. It holds no step machine and makes no
 * routing decision, because there is no longer a decision to make — the
 * landing screen renders one card whose sections always appear in the same
 * order (brand header → rating → platform chooser → private capture) at every
 * rating and before any rating is picked. Deciding *where to send a customer
 * based on what they rated* was this file's whole job, and removing it is the
 * point of the change.
 *
 * Commit model lives in `ReviewLandingScreen`: a star tap is local state, Send
 * or a platform tap commits once, and a platform tap with no rating yet records
 * a click without consuming the single-claim submit slot.
 *
 * A request that is already `COMPLETED` still routes through the same screen in
 * a locked state rather than short-circuiting to a full-page thank-you. A
 * returning customer who wants to leave the public review they meant to leave
 * the first time must still find the chooser; short-circuiting made that tap
 * impossible with nothing in the logs to notice.
 */
export const Route = createFileRoute('/(public)/review/$token')({
  component: ReviewLandingPage,
});

function ReviewLandingPage() {
  const { token } = Route.useParams();
  const { data, isLoading, isError } = useReviewLanding(token);

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </PublicLayout>
    );
  }

  if (isError || !data) {
    return <TokenExpiredState message="This review link is no longer active." />;
  }

  return <ReviewLandingScreen payload={data} token={token} />;
}
